// LWZO (docs/02-konzept.md E27): ein Benutzer - anzeigen, bearbeiten,
// anlegen. Ersetzt den unfertigen Torso in User.tsx (auskommentiertes
// PatchUser, nie geroutet); der bleibt unangetastet, damit ein
// Upstream-Merge ihn nicht mit dieser Datei verwechselt.
//
// Ablauf beim Speichern: nur, was sich geaendert hat, geht als eigener Aufruf
// an den IdP (SetUserProfile, SetUserGroups, SetUserStatus); danach wird der
// Stand neu geladen, damit die Seite zeigt, was der Server hat, nicht, was
// die Maske glaubt. Anlegen ist CreateUser plus je ein AddUserEmail; die
// Adressen sind VERIFIED, weil hier ein Admin fuer sie einsteht - genau wie
// bei idpctl create-user.
import { grpc } from "@improbable-eng/grpc-web";
import { mdiArrowLeft } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useCallback, useEffect, useState } from "react";
import { Nav } from "../account/Nav";
import { idpac, idpc } from "../api";
import { Button, Secondary } from "../Button";
import {
  AuthContext,
  Password_Algorithm,
  Trust,
  User,
  User_Status,
  UserProfile,
} from "../generated/idp/api/idp";
import { Input } from "../Input";
import { FakeUserImage } from "../login/FakeUserImage";
import { goToLocation, goToLocationReplace, useLocation, useQuery } from "../router";
import {
  bytesKey,
  ConfirmingDeleteButton,
  ErrorText,
  FieldLabel,
  IconButton,
  needsLogin,
  SmallButton,
} from "./AdminUI";
import { GroupSelect } from "./GroupSelect";
import { statusLabel, userEmails } from "./Users";

const PREFIX = "/admin/users/";

function trustLabel(t: Trust): string {
  switch (t) {
    case Trust.VETTED:
      return "geprüft";
    case Trust.VERIFIED:
      return "verifiziert";
    default:
      return "unbestätigt";
  }
}

function looksLikeEmail(s: string): boolean {
  const at = s.indexOf("@");
  return at > 0 && at < s.length - 1 && !s.includes(" ");
}

export function UserPage(props: {
  profile?: UserProfile | null;
  onLogout: () => void;
  authenticate: (a: AuthContext) => void;
}): JSX.Element {
  const location = useLocation();
  const isNew = useQuery() === "?new";
  const userID = location.slice(PREFIX.length);
  const authenticate = props.authenticate;

  const [user, setUser] = useState<User | null>(null); // Arbeitskopie
  const [saved, setSaved] = useState<User | null>(null); // Stand vom Server
  const [groups, setGroups] = useState<string[]>([]);
  const [existingEmails, setExistingEmails] = useState<Map<string, string>>(new Map());
  const [pendingEmails, setPendingEmails] = useState<string[]>([]); // nur beim Anlegen
  const [newEmail, setNewEmail] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleErr = useCallback(
    (err: any) => {
      if (needsLogin(err, authenticate)) {
        return;
      }
      setError(String(err?.message ?? err));
    },
    [authenticate]
  );

  const reload = useCallback(async () => {
    const res = await idpc.GetUser({ id: userID });
    const u = res.user ?? null;
    setUser(u);
    setSaved(u);
  }, [userID]);

  useEffect(() => {
    let ignore = false;
    setError(null);
    setNotice(null);
    setNewPassword(null);
    (async () => {
      try {
        const g = await idpac.ListGroups({});
        if (!ignore) setGroups(g.group);
      } catch (err) {
        if (!ignore) handleErr(err);
      }
      if (isNew) {
        // Doppelte Adressen fallen sonst erst beim Anmelden auf: GetUser
        // findet dann irgendeinen der beiden Treffer.
        try {
          const all = await idpac.ListUsers({});
          if (!ignore) {
            const m = new Map<string, string>();
            for (const u of all.user ?? []) {
              for (const e of userEmails(u)) m.set(e.toLowerCase(), u.profile?.name ?? u.id);
            }
            setExistingEmails(m);
          }
        } catch (err) {
          if (!ignore) handleErr(err);
        }
        if (!ignore) {
          const u = User.fromPartial({ id: userID, status: User_Status.OK, profile: { name: "" }, memberOf: [] });
          setUser(u);
          setSaved(null);
          setPendingEmails([]);
        }
        return;
      }
      try {
        await reload();
      } catch (err) {
        if (ignore) return;
        if (err?.code === grpc.Code.NotFound) {
          setLoadError("Diesen Benutzer gibt es nicht.");
        } else {
          handleErr(err);
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [userID, isNew, reload, handleErr]);

  const name = user?.profile?.name ?? "";
  const nameDirty = name !== (saved?.profile?.name ?? "");
  const groupsDirty = JSON.stringify(user?.memberOf ?? []) !== JSON.stringify(saved?.memberOf ?? []);
  const statusDirty = user !== null && saved !== null && user.status !== saved.status;
  const dirty = isNew || nameDirty || groupsDirty || statusDirty;
  const external = saved?.external ?? false;

  const run = useCallback(
    async (work: () => Promise<void>, done?: string) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await work();
        if (done) setNotice(done);
      } catch (err) {
        handleErr(err);
      } finally {
        setBusy(false);
      }
    },
    [handleErr]
  );

  const save = () => {
    if (user === null || busy || !dirty) return;
    const trimmed = name.trim();
    if (trimmed === "") {
      setError("Der Name fehlt.");
      return;
    }
    if (isNew && pendingEmails.length === 0) {
      setError("Mindestens eine E-Mail-Adresse – ohne Adresse kann sich das Konto nie anmelden.");
      return;
    }
    run(async () => {
      if (isNew) {
        await idpac.CreateUser({
          user: { ...user, profile: { ...user.profile, name: trimmed } },
        });
        for (const e of pendingEmails) {
          await idpac.AddUserEmail({ userId: user.id, email: e, trust: Trust.VERIFIED });
        }
        goToLocationReplace({ pathname: PREFIX + user.id, search: "" });
        return;
      }
      if (nameDirty) {
        await idpac.SetUserProfile({ userId: user.id, profile: { ...user.profile, name: trimmed } });
      }
      if (groupsDirty) {
        await idpac.SetUserGroups({ userId: user.id, groupName: user.memberOf });
      }
      if (statusDirty) {
        await idpac.SetUserStatus({ userId: user.id, status: user.status });
      }
      await reload();
    }, "Gespeichert.");
  };

  const addEmail = () => {
    const e = newEmail.trim();
    if (!looksLikeEmail(e)) {
      setError("Das ist keine E-Mail-Adresse.");
      return;
    }
    if (user === null) return;
    if (isNew) {
      const owner = existingEmails.get(e.toLowerCase());
      if (owner !== undefined) {
        setError(`Die Adresse gehört schon zu „${owner}“.`);
        return;
      }
      if (pendingEmails.some((x) => x.toLowerCase() === e.toLowerCase())) {
        setError("Die Adresse steht schon in der Liste.");
        return;
      }
      setError(null);
      setPendingEmails([...pendingEmails, e]);
      setNewEmail("");
      return;
    }
    run(async () => {
      await idpac.AddUserEmail({ userId: user.id, email: e, trust: Trust.VERIFIED });
      setNewEmail("");
      await reload();
    }, "Adresse hinzugefügt.");
  };

  if (loadError !== null) {
    return (
      <Nav profile={props.profile ?? undefined} onLogout={props.onLogout}>
        <h2>{loadError}</h2>
        <Button color={Secondary} onClick={() => goToLocation("/admin/users")}>
          Zur Liste
        </Button>
      </Nav>
    );
  }
  if (user === null) {
    return (
      <Nav profile={props.profile ?? undefined} onLogout={props.onLogout}>
        <p>Lade…</p>
        {error && <ErrorText>{error}</ErrorText>}
      </Nav>
    );
  }

  const readOnly = external || busy;

  return (
    <Nav profile={props.profile ?? undefined} onLogout={props.onLogout}>
      <div style={{ maxWidth: 720 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <IconButton title="Zur Liste" onClick={() => goToLocation("/admin/users")}>
            <Icon size={1} path={mdiArrowLeft} />
          </IconButton>
          <div style={{ width: 48, height: 48 }}>
            {user.profile?.picture ? (
              <img alt={name} src={user.profile?.picture} height="48" style={{ maxWidth: 48 }} />
            ) : (
              <FakeUserImage name={name || user.id} size={48} />
            )}
          </div>
          <div>
            <h1 style={{ margin: 0 }}>{isNew ? "Neuer Benutzer" : name || <i>ohne Namen</i>}</h1>
            <div style={{ color: "#666", fontSize: 13 }}>ID {user.id}</div>
          </div>
        </div>

        {external && (
          <ErrorText>
            Dieses Konto wird von einem externen System verwaltet und lässt sich hier nicht ändern.
          </ErrorText>
        )}

        <Input
          name="name"
          label="Name"
          value={name}
          autoFocus={isNew}
          onChange={(x) =>
            setUser({ ...user, profile: UserProfile.fromPartial({ ...user.profile, name: x }) })
          }
          onEnter={save}
        />

        <FieldLabel htmlFor="status">Status</FieldLabel>
        <select
          id="status"
          value={user.status}
          disabled={readOnly}
          onChange={(e) => setUser({ ...user, status: Number(e.currentTarget.value) as User_Status })}
          style={{ padding: 8, fontSize: "inherit" }}
        >
          <option value={User_Status.OK}>{statusLabel(User_Status.OK)}</option>
          <option value={User_Status.DISABLED}>{statusLabel(User_Status.DISABLED)}</option>
        </select>

        <FieldLabel htmlFor="gruppen">Gruppen</FieldLabel>
        <GroupSelect
          id="gruppen"
          value={user.memberOf ?? []}
          options={groups}
          disabled={readOnly}
          onChange={(v) => setUser({ ...user, memberOf: v })}
        />
        <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
          Gruppen sind Namen am Konto; wer <code>lwzo-admin</code> trägt, braucht bei jeder Anmeldung
          Passwort und zweiten Faktor.
        </div>

        <FieldLabel>E-Mail-Adressen</FieldLabel>
        <ul style={{ listStyle: "none", paddingLeft: 0, marginTop: 0 }}>
          {isNew
            ? pendingEmails.map((e) => (
                <li key={e}>
                  {e}{" "}
                  <SmallButton type="button" onClick={() => setPendingEmails(pendingEmails.filter((x) => x !== e))}>
                    entfernen
                  </SmallButton>
                </li>
              ))
            : (user.emailAddress ?? []).map((e) => (
                <li key={bytesKey(e.id)}>
                  {e.localPart}@{e.domain}{" "}
                  <span style={{ color: "#666" }}>
                    ({trustLabel(e.trust)}
                    {e.lastUse ? `, zuletzt ${e.lastUse.toLocaleDateString("de-CH")}` : ""})
                  </span>{" "}
                  {!external && !e.external && (
                    <SmallButton
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          await idpac.RemoveUserEmail({ userId: user.id, emailId: e.id });
                          await reload();
                        }, "Adresse entfernt.")
                      }
                    >
                      entfernen
                    </SmallButton>
                  )}
                </li>
              ))}
          {!isNew && (user.emailAddress ?? []).length === 0 && (
            <li style={{ color: "#b84646" }}>Keine Adresse – ohne Adresse gibt es keinen Mailcode.</li>
          )}
        </ul>
        {!external && (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, maxWidth: 480 }}>
            <div style={{ flex: 1 }}>
              <Input
                name="email"
                label="Neue Adresse"
                type="email"
                inputMode="email"
                value={newEmail}
                onChange={setNewEmail}
                onEnter={addEmail}
              />
            </div>
            <SmallButton type="button" disabled={busy || newEmail.trim() === ""} onClick={addEmail} style={{ height: 42 }}>
              Hinzufügen
            </SmallButton>
          </div>
        )}

        {!isNew && (
          <>
            <FieldLabel>Anmeldung</FieldLabel>
            <div>
              Passwort:{" "}
              {user.password && user.password.algorithm !== Password_Algorithm.NONE
                ? "gesetzt"
                : "keines (Anmeldung per Mailcode)"}
              {!external && (
                <>
                  {" "}
                  <SmallButton
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const res = await idpac.NewUserPassword({ userId: user.id, forceChange: true });
                        setNewPassword(res.newPassword);
                        await reload();
                      })
                    }
                  >
                    Neues Passwort erzeugen
                  </SmallButton>
                </>
              )}
            </div>
            {newPassword !== null && (
              <div style={{ background: "#fff6d6", border: "1px solid #e0c060", padding: 10, marginTop: 8 }}>
                Einmal sichtbar, danach nicht mehr abrufbar. Auf sicherem Weg übergeben; beim ersten Login muss es
                geändert werden.
                <div style={{ marginTop: 6 }}>
                  <code style={{ fontSize: 16, userSelect: "all" }}>{newPassword}</code>
                </div>
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              Authenticator-Apps:{" "}
              {(user.totpAuthenticator ?? []).length === 0
                ? "keine"
                : (user.totpAuthenticator ?? []).map((a) => a.name || "ohne Namen").join(", ")}
            </div>
            <div>
              Passkeys:{" "}
              {(user.webauthnAuthenticator ?? []).length === 0
                ? "keine"
                : (user.webauthnAuthenticator ?? []).map((a) => a.name || "ohne Namen").join(", ")}
            </div>
            <div style={{ marginTop: 8 }}>
              <label>
                <input
                  type="checkbox"
                  checked={user.enhancedAuthentication}
                  disabled={readOnly}
                  onChange={(e) => {
                    const enabled = e.currentTarget.checked;
                    run(async () => {
                      await idpc.SetUserEnhancedSecurity({ id: user.id, enabled });
                      await reload();
                    }, enabled ? "Erweiterte Sicherheit eingeschaltet." : "Erweiterte Sicherheit ausgeschaltet.");
                  }}
                />{" "}
                Erweiterte Sicherheit (verlangt bei jeder Anmeldung die erhöhte Stufe)
              </label>
            </div>
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}
        {notice && <div style={{ color: "#2e7d32", fontWeight: 600, marginTop: 10 }}>{notice}</div>}

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {!external && (
            <Button color={dirty && !busy ? undefined : Secondary} onClick={save}>
              {busy ? "…" : isNew ? "Anlegen" : "Speichern"}
            </Button>
          )}
          <Button color={Secondary} onClick={() => goToLocation("/admin/users")}>
            {dirty && !isNew ? "Verwerfen" : "Zurück"}
          </Button>
          {!isNew && !external && (
            <div style={{ marginLeft: "auto", marginTop: 15 }}>
              <ConfirmingDeleteButton
                label="Benutzer löschen"
                onDelete={() =>
                  run(async () => {
                    await idpac.DeleteUser({ userId: user.id });
                    goToLocation("/admin/users");
                  })
                }
              />
            </div>
          )}
        </div>
      </div>
    </Nav>
  );
}
